import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Plus, Pencil, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { usePager } from "../components/Pagination";
import useSubmitGuard from "../lib/useSubmitGuard";

const EMPTY = { name: "", code: "", contact_person: "", phone: "", email: "", gst_number: "", address: "", active: true };

export default function Suppliers() {
  // Feb-2026 — submit guard prevents double-tap duplicates.
  const { busy: submitBusy, guard: submitGuard, reset: resetGuard } = useSubmitGuard();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/suppliers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (s) => { setForm(s); setEditing(s.id); setOpen(true); };

  const save = async () => {
    // Wave-B (#9): code is auto-assigned for NEW suppliers — only required
    // when editing an existing row that already has one.
    const required = ["name", "contact_person", "phone", "email", "gst_number", "address"];
    if (form.id && !form.code) required.push("code");
    const missing = required.filter((k) => !(form[k] || "").trim());
    if (missing.length) { toast.error(`Required: ${missing.join(", ")}`); return; }
    try {
      await submitGuard(async (idemKey) => {
        if (editing) await api.put(`/suppliers/${editing}`, form, { headers: { "Idempotency-Key": idemKey } });
        else await api.post("/suppliers", form, { headers: { "Idempotency-Key": idemKey } });
      });
      setOpen(false);
      toast.success(editing ? "Supplier updated" : "Supplier created");
      resetGuard();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete supplier ${s.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success("Supplier deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Wave-J pagination
  const { visibleRows: pagedRows, controls: pageControls } = usePager(rows, 10);

  return (
    <div data-testid="suppliers-page">
      <PageHeader title="Suppliers" subtitle="Vendor master — sources of materials"
        right={<button onClick={openNew} className="bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold" data-testid="new-supplier-btn"><Plus size={14}/> New Supplier</button>}
      /><div className="p-8">
        <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Contact</th>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Phone</th>
                <th className="text-left px-4 py-2">GST</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((s, i) => (
                <tr key={s.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                  <td className="px-4 py-2 font-mono text-[#1565C0]">{s.code}</td>
                  <td className="px-4 py-2 font-medium">{s.name}</td>
                  <td className="px-4 py-2">{s.contact_person}</td>
                  <td className="px-4 py-2 font-mono text-xs">{s.email}</td>
                  <td className="px-4 py-2 font-mono text-xs">{s.phone}</td>
                  <td className="px-4 py-2 font-mono text-xs">{s.gst_number}</td>
                  <td className="px-4 py-2 text-right space-x-2">
                    <button onClick={() => openEdit(s)} className="text-[#1565C0] hover:text-[#0D2B5E]" data-testid={`edit-supplier-${s.code}`}><Pencil size={14}/></button>
                    <button onClick={() => del(s)} className="text-[#C62828]" data-testid={`delete-supplier-${s.code}`}><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-[#0D2B5E]/50">No suppliers yet.</td></tr>}
            </tbody>
          </table>
          {pageControls}
        </div>
      </div>

      {open && (
        <Dialog onClose={() => setOpen(false)} title={editing ? "Edit Supplier" : "New Supplier"}>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="s-name" />
            {/* Wave-B (#9): code is auto-assigned on create; show as read-only stamp once it exists. */}
            {editing ? (
              <Input label="Code (read-only)" value={form.code} onChange={() => {}} testId="s-code" />
            ) : (
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Code</label>
                <div className="border border-dashed border-[#1565C0]/40 bg-[#1565C0]/5 px-3 py-1.5 text-xs text-[#0D2B5E]/70 rounded-sm" data-testid="s-code">Auto-assigned (SUP-NNN)</div>
              </div>
            )}
            <Input label="Contact Person *" value={form.contact_person} onChange={(v) => setForm({ ...form, contact_person: v })} testId="s-contact" />
            <Input label="Phone *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="s-phone" />
            <Input label="Email *" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="s-email" />
            <Input label="GST Number *" value={form.gst_number} onChange={(v) => setForm({ ...form, gst_number: v })} testId="s-gst" />
            <div className="col-span-2">
              <Input label="Address *" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="s-address" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60">Contacts (Purchase / Quality / Accounts)</div>
              <button onClick={() => setForm({ ...form, contacts: [...(form.contacts || []), { label: "Purchase", name: "", email: "", phone: "" }] })} className="text-xs text-[#1565C0] font-semibold uppercase tracking-wider" data-testid="s-add-contact">+ Add contact</button>
            </div>
            {(form.contacts || []).map((c, i) => (
              <div key={i} className="grid grid-cols-5 gap-2 mb-2">
                <select value={c.label} onChange={(e) => setForm({ ...form, contacts: form.contacts.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x) })} className="border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm">
                  <option>Purchase</option><option>Quality</option><option>Accounts</option><option>Other</option>
                </select>
                <input value={c.name} onChange={(e) => setForm({ ...form, contacts: form.contacts.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x) })} placeholder="Name" className="border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm"/>
                <input value={c.email} onChange={(e) => setForm({ ...form, contacts: form.contacts.map((x, idx) => idx === i ? { ...x, email: e.target.value } : x) })} placeholder="Email" className="border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono"/>
                <input value={c.phone} onChange={(e) => setForm({ ...form, contacts: form.contacts.map((x, idx) => idx === i ? { ...x, phone: e.target.value } : x) })} placeholder="Phone" className="border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono"/>
                <button onClick={() => setForm({ ...form, contacts: form.contacts.filter((_, idx) => idx !== i) })} className="text-[#C62828] text-xs">Remove</button>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={save} disabled={submitBusy} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed" data-testid="save-supplier-btn">{submitBusy ? "Saving…" : "Save"}</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

export function Dialog({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0D2B5E]/70 p-4" onClick={onClose}>
      <div className={`bg-white rounded-sm shadow-2xl ${wide ? "max-w-4xl" : "max-w-2xl"} w-full`} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#EEEEEE] flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#0D2B5E]">{title}</h3>
          <button onClick={onClose} className="text-[#0D2B5E]/50 hover:text-[#C62828]" data-testid="dialog-close"><X size={18}/></button>
        </div>
        <div className="p-6 max-h-[75vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

export function Input({ label, value, onChange, type = "text", testId, disabled, max, min, placeholder, step }) {
  // Number inputs default to `step="any"` so decimals (up to 3 places, see
  // lib/format.js) are accepted everywhere unless caller overrides.
  const effectiveStep = step !== undefined ? step : (type === "number" ? "any" : undefined);
  return (
    <div>
      <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} disabled={disabled} max={max} min={min} placeholder={placeholder} step={effectiveStep}
        className="w-full border border-[#EEEEEE] focus:border-[#1565C0] outline-none px-3 py-2 text-sm font-mono rounded-sm disabled:bg-[#F5F5F5]" />
    </div>
  );
}
